import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  Card,
  CardBody,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem, } from "reactstrap";
import { toast } from "react-toastify";
import TableContainer from "../../Components/Common/TableContainer";
import { useProfile } from "../../Components/Hooks/UserHooks";
import { useNavigate } from "react-router-dom";
import DeleteModal from "../../Components/Common/DeleteModal";
import CancelModal from "../../Components/Common/CancelModal";
import InvoiceModalPdf from "../../Components/Common/invoiceModalPdf";
import { createSelector } from "reselect";
import { useSelector, useDispatch } from "react-redux";
import {
  deleteInvoice as onDeleteInvoice,
  getInvoices as onGetInvoices,
  setInvoiceAsPaid as onSetInvoiceAsPaid,
  cancelInvoice as onCancelInvoice,
} from "../../slices/thunks";
import { unlinkInvoiceById as onUnlinkInvoiceById } from "../../slices/smartbill/thunk";
import { useCurrencyFormatter } from "../../Components/Hooks/useCurrencyFormatter";

const selectInvoiceState = (state) => state.Invoices || {};
const selectInvoiceProperties = createSelector(
  selectInvoiceState,
  (invoice) => ({
    invoices: invoice.invoices || [],
    loading: invoice.loading || false,
    error: invoice.error || null,
    filters: invoice.filters || {},
    pagination: invoice.pagination || {},
  })
);

const EmptyState = () => (
  <div className="text-center py-5">
    <div className="mb-3">
      <i className="ri-file-list-3-line display-4 text-muted"></i>
    </div>
    <h5 className="text-muted mb-2">No Invoices Found</h5>
    <p className="text-muted mb-3">
      No invoices available. Add your first invoice to get started.
    </p>
  </div>
);

const LoadingSpinner = () => (
  <div className="d-flex justify-content-center align-items-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

const formatDate = (date) => {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getPaymentStatus = (amountPaid, amount) => {
  if (amountPaid === 0) {
    return {
      status: "unpaid",
      iconClass: "ri-time-line",
      textClass: "text-warning",
      statusText: "Pending",
    };
  }
  if (amountPaid >= amount) {
    return {
      status: "paid",
      iconClass: "ri-check-circle-fill",
      textClass: "text-success",
      statusText: "Paid",
    };
  }
  return {
    status: "partial",
    iconClass: "ri-check-double-line",
    textClass: "text-info",
    statusText: "Partial",
  };
};

const useInvoiceActions = (dispatch, fetchInvoices) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);

  const handleDelete = useCallback(
    async (invoice) => {
      if (!invoice || isDeleting) return { success: false };
      try {
        setIsDeleting(true);
        await dispatch(onDeleteInvoice(invoice.id)).unwrap();
        await fetchInvoices();
        return { success: true };
      } catch (error) {
        console.error("Error deleting invoice:", error);
        return { success: false, code: error?.code, message: error?.message };
      } finally {
        setIsDeleting(false);
      }
    },
    [dispatch, isDeleting, fetchInvoices]
  );

  const handleCancel = useCallback(
    async (invoice) => {
      if (!invoice || isCancelling) return;
      try {
        setIsCancelling(true);
        await dispatch(onCancelInvoice(invoice.id));
        await fetchInvoices();
      } catch (error) {
        console.error("Error cancelling invoice:", error);
      } finally {
        setIsCancelling(false);
      }
    },
    [dispatch, isCancelling, fetchInvoices]
  );

  const handleMarkAsPaid = useCallback(
    async (invoice) => {
      if (isMarkingPaid || invoice.paid) return;
      try {
        setIsMarkingPaid(true);
        await dispatch(onSetInvoiceAsPaid(invoice.id));
      } catch (error) {
        console.error("Error marking invoice as paid:", error);
      } finally {
        setIsMarkingPaid(false);
      }
    },
    [dispatch, isMarkingPaid]
  );

  const handleUnlinkFromRecords = useCallback(
    async (invoice) => {
      if (!invoice || isUnlinking) return { success: false };
      try {
        setIsUnlinking(true);
        await dispatch(onUnlinkInvoiceById(invoice.id)).unwrap();
        await fetchInvoices();
        toast.success("Invoice removed from records", { autoClose: 3000 });
        return { success: true };
      } catch (error) {
        console.error("Error unlinking invoice:", error);
        toast.error(error?.message || "Failed to remove invoice from records", { autoClose: 3000 });
        return { success: false };
      } finally {
        setIsUnlinking(false);
      }
    },
    [dispatch, isUnlinking, fetchInvoices]
  );

  return {
    handleDelete,
    handleCancel,
    handleMarkAsPaid,
    handleUnlinkFromRecords,
    isDeleting,
    isCancelling,
    isMarkingPaid,
    isUnlinking,
  };
};

const PaymentStatusCell = ({ row, formatCurrency }) => {
  const { paid_at, amount = 0, amount_paid = 0, canceled, cancelled, currency = "EUR" } = row.original;
  const isCancelled = canceled || cancelled;

  if (isCancelled) {
    return (
      <div className="d-flex align-items-center">
        <i className="ri-close-circle-fill text-muted me-2"></i>
        <span className="text-muted fw-semibold">Canceled</span>
      </div>
    );
  }

  const { status, iconClass, textClass, statusText } = getPaymentStatus(amount_paid, amount);

  return (
    <div className="d-flex align-items-center">
      <i className={`${iconClass} ${textClass} me-2`}></i>
      <span className={`${textClass} fw-semibold`}>{statusText}</span>
      {status === "paid" && paid_at && (
        <small className="text-muted ms-1">({formatDate(paid_at)})</small>
      )}
      {status === "partial" && (
        <small className="text-muted ms-1">
          ({formatCurrency(amount_paid, currency, false, false)} / {formatCurrency(amount, currency, false, false)})
        </small>
      )}
    </div>
  );
};

const ClickableCell = ({ value, id, path, navigate, title }) => {
  if (value === "—" || !id) {
    return <span className="fw-semibold">{value}</span>;
  }

  return (
    <span
      className="fw-semibold text-primary text-decoration-underline"
      onClick={(e) => {
        e.stopPropagation();
        navigate(`${path}/${id}`);
      }}
      style={{ cursor: "pointer" }}
      onMouseEnter={(e) => (e.target.style.backgroundColor = "#e3f2fd")}
      onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
      title={title}
    >
      {value}
    </span>
  );
};

const ActionsCell = ({
  invoice,
  isAdmin,
  handleViewPdf,
  handleMarkAsPaid,
  onClickCancel,
  onClickDelete,
  isMarkingPaid,
  isCancelling,
  isDeleting,
}) => {
  if (!isAdmin) return null;

  const isPaid = invoice.paid || invoice.status === "paid";
  const isFirst = invoice.is_first || false;
  const isCancelled = invoice.canceled || invoice.cancelled;

  return (
    <div className="text-center">
      <UncontrolledDropdown strategy="fixed">
        <DropdownToggle
          tag="button"
          className="btn btn-sm btn-soft-secondary"
          onClick={(e) => e.stopPropagation()}
        >
          <i className="ri-more-fill align-middle"></i>
        </DropdownToggle>
        <DropdownMenu end>
          {!isCancelled && (
            <DropdownItem
              onClick={(e) => {
                e.stopPropagation();
                handleViewPdf(invoice.id, `${invoice.series_name}/${invoice.number}`);
              }}
            >
              <i className="ri-file-pdf-line align-middle me-2 text-success"></i>
              View PDF
            </DropdownItem>
          )}
          
          {!isPaid && !isCancelled && (
            <>
              {/* <DropdownItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleMarkAsPaid(invoice);
                }}
                disabled={isMarkingPaid}
              >
                <i className="ri-check-line align-middle me-2 text-success"></i>
                {isMarkingPaid ? "Marking..." : "Mark as Paid"}
              </DropdownItem> */}
              
              <DropdownItem divider />
              
              <DropdownItem
                onClick={(e) => {
                  e.stopPropagation();
                  onClickCancel(invoice);
                }}
                disabled={isCancelling}
              >
                <i className="ri-close-line align-middle me-2 text-warning"></i>
                {isCancelling ? "Cancelling..." : "Cancel Invoice"}
              </DropdownItem>
            </>
          )}
          
          {!isCancelled && isFirst && (
            <>
              <DropdownItem divider />
              <DropdownItem
                onClick={(e) => {
                  e.stopPropagation();
                  onClickDelete(invoice);
                }}
                disabled={isDeleting}
                className="text-danger"
              >
                <i className="ri-delete-bin-5-fill align-middle me-2"></i>
                {isDeleting ? "Deleting..." : "Delete Invoice"}
              </DropdownItem>
            </>
          )}
        </DropdownMenu>
      </UncontrolledDropdown>
    </div>
  );
};

const InvoiceTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { invoices, loading } = useSelector(selectInvoiceProperties);
  const { userProfile } = useProfile();
  const { formatCurrency } = useCurrencyFormatter();

  const [deleteModal, setDeleteModal] = useState(false);
  const [unlinkOfferModal, setUnlinkOfferModal] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedInvoiceName, setSelectedInvoiceName] = useState("");

  const isAdmin = userProfile?.user?.groups?.some((group) =>
    ["Admin", "Editor"].includes(group)
  );

  const fetchInvoices = useCallback(async () => {
    try {
      await dispatch(onGetInvoices());
    } catch (error) {
      console.error("Failed to fetch invoices:", error);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const {
    handleDelete,
    handleCancel,
    handleMarkAsPaid,
    handleUnlinkFromRecords,
    isDeleting,
    isCancelling,
    isMarkingPaid,
    isUnlinking,
  } = useInvoiceActions(dispatch, fetchInvoices);

  const tableData = useMemo(() => {
    if (!invoices?.length) return [];

    return invoices.map((invoice) => ({
      ...invoice,
      created_at_date: new Date(invoice.created_at || Date.now()),
      due_date_date: invoice.due_date ? new Date(invoice.due_date) : null,
      formattedDate: formatDate(invoice.created_at),
      formattedDueDate: formatDate(invoice.due_date),
    }));
  }, [invoices]);

  const handleViewPdf = useCallback((invoiceId, invoiceName = "") => {
    setSelectedInvoice(invoiceId);
    setSelectedInvoiceName(invoiceName);
    setShowPdfModal(true);
  }, []);

  const handleRowClick = useCallback((row) => {
    navigate(`/invoices/${row.original.id}`);
  }, [navigate]);

  const onClickDelete = useCallback((invoice) => {
    setSelectedInvoice(invoice);
    setDeleteModal(true);
  }, []);

  const onClickCancel = useCallback((invoice) => {
    setSelectedInvoice(invoice);
    setCancelModal(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    const result = await handleDelete(selectedInvoice);
    setDeleteModal(false);
    if (!result.success && result.code === "INVOICE_NOT_FOUND_IN_SMARTBILL") {
      setUnlinkOfferModal(true);
    } else {
      setSelectedInvoice(null);
    }
  }, [handleDelete, selectedInvoice]);

  const handleConfirmUnlinkOffer = useCallback(async () => {
    await handleUnlinkFromRecords(selectedInvoice);
    setUnlinkOfferModal(false);
    setSelectedInvoice(null);
  }, [handleUnlinkFromRecords, selectedInvoice]);

  const handleConfirmCancel = useCallback(async () => {
    await handleCancel(selectedInvoice);
    setCancelModal(false);
  }, [handleCancel, selectedInvoice]);

  const columns = useMemo(
    () => [
      {
        header: "Invoice #",
        accessorKey: "number",
        enableColumnFilter: false,
        cell: ({ getValue, row }) => (
          <span className={`fw-semibold ${row.original.canceled || row.original.cancelled ? "text-muted" : ""}`}>
            {getValue() || "—"}
          </span>
        ),
      },
      {
        header: "Series",
        accessorKey: "series_name",
        enableColumnFilter: false,
        cell: ({ getValue, row }) => (
          <span className={`fw-semibold ${row.original.canceled || row.original.cancelled ? "text-muted" : ""}`}>
            {getValue() || "—"}
          </span>
        ),
      },
      {
        header: "Payment Status",
        accessorKey: "paid",
        enableColumnFilter: false,
        cell: ({ row }) => <PaymentStatusCell row={row} formatCurrency={formatCurrency} />,
      },
      {
        header: "Contract",
        accessorKey: "contract_details",
        enableColumnFilter: false,
        cell: ({ getValue, row }) => (
          <ClickableCell
            value={getValue() || row.original.contract?.pvc_number || "—"}
            id={row.original.contract?.id}
            path="/contracts"
            navigate={navigate}
            title="Click to view contract details"
          />
        ),
      },
      {
        header: "Client",
        accessorKey: "client_details",
        enableColumnFilter: false,
        cell: ({ getValue, row }) => (
          <ClickableCell
            value={getValue() || row.original.client?.name || "—"}
            id={row.original.client?.id}
            path="/client-details"
            navigate={navigate}
            title="Click to view client details"
          />
        ),
      },
      {
        header: "Amount",
        accessorKey: "amount",
        enableColumnFilter: false,
        cell: ({ getValue, row }) => (
          <span className={`fw-semibold ${row.original.canceled || row.original.cancelled ? "text-muted" : ""}`}>
            {formatCurrency(getValue() || 0, row.original.currency || "EUR", false, false)}
          </span>
        ),
      },
      {
        header: "Tax Amount",
        accessorKey: "tax_amount",
        enableColumnFilter: false,
        cell: ({ getValue, row }) => (
          <span className={`fw-semibold ${row.original.canceled || row.original.cancelled ? "text-muted" : ""}`}>
            {formatCurrency(getValue() || 0, row.original.currency || "EUR", false, false)}
          </span>
        ),
      },
      {
        header: "Amount Paid",
        accessorKey: "amount_paid",
        enableColumnFilter: false,
        cell: ({ getValue, row }) => (
          <span className={`fw-semibold ${row.original.canceled || row.original.cancelled ? "text-muted" : ""}`}>
            {formatCurrency(getValue() || 0, row.original.currency || "EUR", false, false)}
          </span>
        ),
      },
      {
        header: "Due Date",
        accessorKey: "due_date_date",
        enableColumnFilter: false,
        cell: ({ row }) => row.original.formattedDueDate,
        sortingFn: (rowA, rowB, columnId) => {
          const dateA = rowA.getValue(columnId);
          const dateB = rowB.getValue(columnId);
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateA.getTime() - dateB.getTime();
        },
      },
      {
        header: "Created Date",
        accessorKey: "created_at_date",
        enableColumnFilter: false,
        cell: ({ row }) => row.original.formattedDate,
        sortingFn: (rowA, rowB, columnId) => {
          const dateA = rowA.getValue(columnId);
          const dateB = rowB.getValue(columnId);
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateA.getTime() - dateB.getTime();
        },
      },
      {
        id: "actions",
        enableColumnFilter: false,
        header: "",
        size: 30,
        cell: ({ row }) => (
          <ActionsCell
            invoice={row.original}
            isAdmin={isAdmin}
            handleViewPdf={handleViewPdf}
            handleMarkAsPaid={handleMarkAsPaid}
            onClickCancel={onClickCancel}
            onClickDelete={onClickDelete}
            isMarkingPaid={isMarkingPaid}
            isCancelling={isCancelling}
            isDeleting={isDeleting}
          />
        ),
      },
    ],
    [isAdmin, formatCurrency, navigate, handleViewPdf, handleMarkAsPaid, onClickCancel, onClickDelete, isMarkingPaid, isCancelling, isDeleting]
  );

  return (
    <Card id="invoiceList" className="shadow-sm">
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleConfirmDelete}
        onCloseClick={() => setDeleteModal(false)}
        isDeleting={isDeleting}
      />

      <DeleteModal
        show={unlinkOfferModal}
        onDeleteClick={handleConfirmUnlinkOffer}
        onCloseClick={() => {
          setUnlinkOfferModal(false);
          setSelectedInvoice(null);
        }}
        isDeleting={isUnlinking}
        title="Invoice not found in SmartBill"
        message={
          selectedInvoice
            ? `Do you want to remove the invoice ${selectedInvoice.series_name}/${selectedInvoice.number} from records?`
            : ""
        }
        confirmLabel="Remove from records"
        confirmingLabel="Removing..."
      />

      <CancelModal
        show={cancelModal}
        onCancelClick={handleConfirmCancel}
        onCloseClick={() => setCancelModal(false)}
        isCancelling={isCancelling}
      />

      <InvoiceModalPdf
        isOpen={showPdfModal}
        toggle={() => setShowPdfModal(false)}
        invoiceId={selectedInvoice}
        invoiceName={selectedInvoiceName}
      />

      <CardBody className="pt-0">
        {loading ? (
          <LoadingSpinner />
        ) : tableData?.length > 0 ? (
          <TableContainer
            columns={columns}
            data={tableData}
            isGlobalFilter={true}
            isAddclient={false}
            customPageSize={10}
            // handleRowClick={handleRowClick}
            customTableRowProps={(row) => {
              const isCancelled = row.original.canceled || row.original.cancelled;
              return {
                className: `cursor-pointer hover-row ${isCancelled ? "text-muted" : ""}`,
                style: {
                  cursor: "pointer",
                  opacity: isCancelled ? 0.6 : 1,
                },
              };
            }}
            divClass="table-card mt-3 mb-1"
            tableClass="align-middle table-nowrap"
            theadClass="table-light text-muted"
            SearchPlaceholder="Search invoices..."
          />
        ) : (
          <EmptyState />
        )}
      </CardBody>
    </Card>
  );
};

export default InvoiceTable;